import React, { MouseEvent } from 'react';
import { StatusBadge } from './StatusBadge';

/**
 * Document type definition matching Architecture section 4.7
 */
export type DocumentType = 'allocation_declaration' | 'inspection_certificate' | 'warehouse_receipt' | 'certificate_of_origin' | 'other';
export type DocumentStatus = 'pending' | 'approved' | 'rejected';

export interface Document {
  id: string;
  ownerUserId: string;
  productId?: string;
  transactionId?: string;
  type: DocumentType;
  fileName: string;
  url: string;
  status: DocumentStatus;
  uploadedAt: Date;
  approvedAt?: Date;
}

interface DocumentCardProps {
  document: Document;
  isExpanded?: boolean;
  onApprove?: () => void;
  onDisapprove?: () => void;
  onClick?: () => void;
}

/**
 * DocumentCard component - Displays a single document in the document list with inline expansion
 * 
 * Features:
 * - Document type name display
 * - Status badge (Pending/Approved)
 * - Clickable row that expands inline to show document preview
 * - Approve/Disapprove buttons in expanded state
 * - Proper spacing and visual hierarchy
 * - Hover states for interactive elements
 * - Accessibility features (ARIA labels, keyboard navigation)
 * 
 * Usage:
 * - Clicking the row (outside buttons) triggers onClick callback to toggle expansion
 * - Expanded view shows document preview with approve/disapprove actions
 */
export const DocumentCard = ({ document, isExpanded = false, onApprove, onDisapprove, onClick }: DocumentCardProps) => {
  // Map document type to display name
  const getDocumentTypeName = (type: DocumentType): string => {
    const typeNames: Record<DocumentType, string> = {
      allocation_declaration: 'Allocation declaration',
      inspection_certificate: 'Inspection certificate',
      warehouse_receipt: 'Warehouse receipt(s)',
      certificate_of_origin: 'Certificate of origin',
      other: 'Other',
    };
    return typeNames[type];
  };

  const canApprove = document.status === 'pending' && onApprove !== undefined;
  const canDisapprove = document.status === 'approved' && onDisapprove !== undefined;

  const handleCardClick = (e: MouseEvent<HTMLDivElement>) => {
    // Don't trigger card click if clicking on buttons
    if ((e.target as HTMLElement).closest('button')) {
      return;
    }
    onClick?.();
  };

  return (
    <div 
      className={`border border-border rounded-lg overflow-hidden transition-all ${
        onClick ? 'hover:bg-background-secondary cursor-pointer' : ''
      }`}
      role="article"
      aria-label={`Document: ${getDocumentTypeName(document.type)}, Status: ${document.status}`}
      aria-expanded={isExpanded}
    >
      {/* Header Row - Always visible */}
      <div
        className="flex items-center justify-between p-4"
        onClick={onClick ? handleCardClick : undefined}
        tabIndex={onClick ? 0 : undefined}
        onKeyDown={onClick ? (e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            onClick();
          }
        } : undefined}
      >
        <div className="flex items-center gap-4 flex-1 min-w-0">
          {/* Document Type */}
          <div className="flex-1 min-w-0">
            <p className="text-base font-medium text-text-primary truncate">
              {getDocumentTypeName(document.type)}
            </p>
          </div>

          {/* Status Badge */}
          <div className="flex-shrink-0">
            <StatusBadge status={document.status} />
          </div>
        </div>
      </div>

      {/* Expanded Content - Shown when isExpanded is true */}
      {isExpanded && (
        <div className="border-t border-border bg-white p-6">
          {/* Document Content Preview */}
          <div className="border border-border rounded-lg overflow-hidden bg-background-secondary mb-6">
            <div className="w-full min-h-[400px] max-h-[600px] flex items-center justify-center bg-background-secondary">
              {document.url ? (
                <img
                  src={document.url}
                  alt={getDocumentTypeName(document.type)}
                  className="max-w-full max-h-full object-contain"
                />
              ) : (
                <div className="text-center p-8">
                  <div className="text-6xl mb-4">📄</div>
                  <p className="text-base text-text-secondary">
                    Document Preview
                  </p>
                  <p className="text-sm text-text-tertiary mt-2">
                    {document.fileName}
                  </p>
                </div>
              )}
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex flex-col gap-3">
            {/* Approve Button - shown when pending */}
            {canApprove && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  onApprove?.();
                }}
                className="w-full px-6 py-3 bg-primary text-white rounded-lg hover:bg-primary-dark transition-colors font-medium text-base focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2"
                aria-label={`Approve ${getDocumentTypeName(document.type)}`}
              >
                Approve
              </button>
            )}

            {/* Disapprove Button - shown when approved */}
            {canDisapprove && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  onDisapprove?.();
                }}
                className="w-full px-6 py-3 bg-warning text-white rounded-lg hover:bg-warning/90 transition-colors font-medium text-base focus:outline-none focus:ring-2 focus:ring-warning focus:ring-offset-2"
                aria-label={`Disapprove ${getDocumentTypeName(document.type)}`}
              >
                Disapprove
              </button>
            )}

            {/* Report an issue link */}
            <button
              onClick={(e) => {
                e.stopPropagation();
                e.preventDefault();
                // TODO: Implement report issue functionality
              }}
              className="text-sm text-text-secondary hover:text-text-primary underline text-center focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 rounded"
              aria-label="Report an issue with this document"
            >
              Report an issue
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

